import {
  HttpApiError,
  NetworkApiError,
  ParsedApiError,
  UnknownApiError,
} from "./types";
import { isParsedApiError } from "./utils";

/** Distinguishes machine codes ("quota_limit_exceeded") from human-readable text. */
const CODE_PATTERN = /^[a-z0-9_.-]+$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

interface CollectedPayload {
  codes: string[];
  serverMessage: string | null;
  fieldErrors: Record<string, string[]>;
  details: Record<string, unknown> | null;
}

const collectString = (value: string, acc: CollectedPayload) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return;
  }
  if (CODE_PATTERN.test(trimmed)) {
    if (!acc.codes.includes(trimmed)) {
      acc.codes.push(trimmed);
    }
  } else if (!acc.serverMessage) {
    acc.serverMessage = trimmed;
  }
};

const collectFieldError = (
  field: string,
  message: string,
  acc: CollectedPayload
) => {
  acc.fieldErrors[field] = [...(acc.fieldErrors[field] || []), message];
};

const collectFromPayload = (data: unknown): CollectedPayload => {
  const acc: CollectedPayload = {
    codes: [],
    serverMessage: null,
    fieldErrors: {},
    details: null,
  };
  if (typeof data === "string") {
    collectString(data, acc);
    return acc;
  }
  if (!isRecord(data)) {
    return acc;
  }
  const { errors, error, message } = data as {
    errors?: unknown;
    error?: unknown;
    message?: unknown;
  };

  if (typeof errors === "string") {
    collectString(errors, acc);
  } else if (Array.isArray(errors)) {
    errors.forEach((entry) => {
      if (typeof entry === "string") {
        collectString(entry, acc);
        return;
      }
      if (!isRecord(entry) || typeof entry.message !== "string") {
        return;
      }
      collectString(entry.message, acc);
      if (typeof entry.field === "string") {
        collectFieldError(entry.field, entry.message, acc);
      }
    });
  } else if (isRecord(errors)) {
    if (typeof errors.error === "string") {
      collectString(errors.error, acc);
      const { error: nestedError, ...rest } = errors;
      if (Object.keys(rest).length > 0) {
        acc.details = { ...acc.details, ...rest };
      }
    } else {
      Object.entries(errors).forEach(([field, value]) => {
        if (typeof value === "string") {
          collectFieldError(field, value, acc);
        } else if (Array.isArray(value)) {
          value.forEach((item) => {
            if (typeof item === "string") {
              collectFieldError(field, item, acc);
            }
          });
        }
      });
    }
  }

  if (typeof error === "string") {
    collectString(error, acc);
  } else if (isRecord(error)) {
    if (typeof error.error_user_msg === "string" && !acc.serverMessage) {
      acc.serverMessage = error.error_user_msg;
    } else if (typeof error.message === "string") {
      collectString(error.message, acc);
    }
    const providerDetails: Record<string, unknown> = {};
    if ("code" in error) {
      providerDetails.providerCode = error.code;
    }
    if ("error_subcode" in error) {
      providerDetails.providerSubcode = error.error_subcode;
    }
    if (Object.keys(providerDetails).length > 0) {
      acc.details = { ...acc.details, ...providerDetails };
    }
  }

  if (!acc.serverMessage && !acc.codes.length) {
    if (typeof message === "string") {
      collectString(message, acc);
    } else if (isRecord(message) && typeof message.error === "string") {
      collectString(message.error, acc);
      const { error: nestedError, ...rest } = message;
      if (Object.keys(rest).length > 0) {
        acc.details = { ...acc.details, ...rest };
      }
    }
  }
  return acc;
};

const parseHttpResponse = (
  response: Record<string, unknown>,
  raw: unknown
): HttpApiError => {
  const config = isRecord(response.config) ? response.config : undefined;
  const collected = collectFromPayload(response.data);
  return {
    __parsedApiError: true,
    type: "http",
    status: response.status as number,
    url: typeof config?.url === "string" ? config.url : undefined,
    method: typeof config?.method === "string" ? config.method : undefined,
    code: collected.codes[0] || null,
    codes: collected.codes,
    serverMessage: collected.serverMessage,
    fieldErrors: collected.fieldErrors,
    details: collected.details,
    data: response.data,
    raw,
  };
};

const networkError = (raw: unknown, simulated?: boolean): NetworkApiError => ({
  __parsedApiError: true,
  type: "network",
  simulated: !!simulated,
  raw,
});

const unknownError = (
  raw: unknown,
  message: string | null,
  canceled?: boolean
): UnknownApiError => ({
  __parsedApiError: true,
  type: "unknown",
  canceled: !!canceled,
  message,
  raw,
});

/**
 * Normalizes any API failure into a ParsedApiError. Accepts (and is idempotent over):
 * already-parsed errors, RTK Query errors (raw AxiosResponse objects from baseQuery),
 * the baseQuery `__NETWORK_ERROR` sentinel, full AxiosError objects, and unknown throws.
 */
export const parseApiError = (error: unknown): ParsedApiError => {
  if (isParsedApiError(error)) {
    return error;
  }

  if (isRecord(error)) {
    if (error.__NETWORK_ERROR) {
      if (error.code === "ERR_CANCELED") {
        return unknownError(error, null, true);
      }
      return networkError(error, !!error._isSimulated);
    }

    // Demo-mode request interceptor rejects with a bare `{ config, _isSimulated: true }`.
    if (error._isSimulated) {
      return networkError(error, true);
    }

    // Full AxiosError (thrown by axios or rethrown by interceptors).
    if (error.isAxiosError === true) {
      const { response } = error;
      if (isRecord(response) && typeof response.status === "number") {
        return parseHttpResponse(response, error);
      }
      if (error.code === "ERR_CANCELED") {
        return unknownError(error, null, true);
      }
      return networkError(error);
    }

    // RTK Query error object — the AxiosResponse returned by baseQuery.
    if (typeof error.status === "number" && "data" in error) {
      return parseHttpResponse(error, error);
    }
  }

  if (error instanceof Error) {
    return unknownError(
      error,
      error.message || null,
      error.name === "AbortError"
    );
  }
  return unknownError(error, typeof error === "string" ? error : null);
};
